/**
 * Looking at an attachment before sending it, or after receiving it (D40).
 *
 * Two routes, because the file is in two different places:
 *
 * - **Received or sent**: the server streams it from Zoho and the browser shows
 *   it (`/preview/`). Only the types below; everything else downloads, because
 *   an HTML or SVG file rendered on this origin would run as the seller (§8.4).
 * - **Just attached**: the file is still in the browser, so it is shown from
 *   there and Zoho is not asked at all. A draft reopened later has neither —
 *   those files can be looked at once the email has been sent.
 *
 * The list mirrors `PREVIEWABLE` in `crm26/mail/api/attachments.py`. The server
 * is the one that decides: it answers 404 for anything not on its own list.
 */
const PREVIEWABLE: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  txt: "text/plain",
};

/** The type to show `name` as, or `null` when it can only be downloaded. */
export function previewType(name: string): string | null {
  const suffix = name.toLowerCase().split(".").pop() ?? "";
  return PREVIEWABLE[suffix] ?? null;
}

export function downloadUrl(id: number): string {
  return `/api/mail/attachments/${id}/`;
}

export function previewUrl(id: number): string {
  return `/api/mail/attachments/${id}/preview/`;
}

/** How long a file kept in memory stays reachable by the tab showing it. */
const BLOB_MINUTES = 5;

/**
 * Show a file the seller has just attached, in a new tab.
 *
 * The type comes from the name, never from the file itself: an `.pdf` holding
 * HTML is shown as a broken PDF rather than as a page running on this origin.
 */
export function openLocalPreview(file: File, name: string): boolean {
  const type = previewType(name);
  if (type === null) return false;
  const url = URL.createObjectURL(new Blob([file], { type }));
  window.open(url, "_blank", "noopener");
  // The tab has it by now; this only stops the page from holding the bytes.
  setTimeout(() => URL.revokeObjectURL(url), BLOB_MINUTES * 60_000);
  return true;
}
